import React, { FC, HTMLAttributes, useCallback } from 'react';
import { useDispatchRequest } from '@redux-requests/react';
import { useDispatch, useSelector } from 'react-redux';
import { FORM_ERROR } from 'final-form';
import { Field, Form } from 'react-final-form';
import classNames from 'classnames';

import { Button, Heading } from 'components/ui';
import { Error, ValidateInfo } from 'components';
import { newPasswordRules, passwordFormFields, passwordFormFieldsValues } from './constants';
import { saveUserData, togglePasswordModal } from 'modules/layout/store/actions';
import BasicModal from '../BasicModal/BasicModal';
import { RootState } from 'store/rootReducer';
import { TextInputWrapper } from 'components/forms';
import { passwordModalValidator } from './changePasswordValidator';
import { t } from 'utils';

import styles from './PasswordModal.module.scss';

export interface IPasswordModal extends HTMLAttributes<HTMLDivElement> {}

const PasswordModal: FC<IPasswordModal> = (props) => {
  const { className } = props;
  const dispatch = useDispatch();
  const dispatchRequest = useDispatchRequest();
  const open = useSelector((state: RootState) => state.layout.passwordModal);
  const rootClassName = classNames(styles.root, className);

  const handleCloseModal = useCallback(() => {
    dispatch(togglePasswordModal(false));
  }, [dispatch]);

  const handlePasswordSubmit = useCallback(
    async (values) => {
      const { password, repeatPassword } = values;

      if (password !== repeatPassword) {
        return { [FORM_ERROR]: t('errors.passwordReset.passwordsMismatch') };
      }

      const { data, error } = await dispatchRequest(saveUserData(values));

      if (error) {
        const errorCode = error.data?.errors[0]?.extensions?.code;
        if (errorCode === 'PasswordInvalid') {
          return { [FORM_ERROR]: t('errors.passwordReset.invalidCurrentPassword') };
        } else {
          return { [FORM_ERROR]: error.data.errors[0].message };
        }
      }

      if (data) {
        handleCloseModal();
      }
    },
    [dispatchRequest, handleCloseModal]
  );

  return (
    <BasicModal className={rootClassName} open={open} onClose={handleCloseModal}>
      <Heading className='mb-3' icon='shield-alt'>
        {t('global.password.reset')}
      </Heading>
      <Form
        onSubmit={handlePasswordSubmit}
        validate={passwordModalValidator}
        render={({ handleSubmit, errors = {}, error, submitting, submitError, submitFailed }) => (
          <form onSubmit={handleSubmit} noValidate>
            {passwordFormFieldsValues.map((field) => (
              <Field
                type='password'
                component={TextInputWrapper}
                key={field.name}
                label={field.label}
                placeholder={field.placeholder}
                name={field.name}
                hideErrors
                className={field.className}
              />
            ))}
            <Error className='mb-2' isError={submitError}>
              {submitError}
            </Error>
            <Error className='mb-2' isError={submitFailed && error}>
              {error}
            </Error>
            <div>
              {newPasswordRules.map((rule) => (
                <ValidateInfo
                  isValid={
                    !errors[passwordFormFields.newPassword.name]?.includes(t('errors.required')) &&
                    !errors[passwordFormFields.oldPassword.name]?.includes(t('errors.required')) &&
                    !errors[passwordFormFields.newPassword.name]?.includes(t(rule.description))
                  }
                  key={rule.name}
                  className='mb-1'
                >
                  {t(rule.description)}
                </ValidateInfo>
              ))}
            </div>
            <div className='mt-4'>
              <Button
                className='mr-2'
                onClick={handleCloseModal}
                type='button'
                variant='secondary'
                inline
                bold
              >
                {t('cancel')}
              </Button>
              <Button disabled={submitting || Object.keys(errors).length > 0} inline>
                {t('save')}
              </Button>
            </div>
          </form>
        )}
      />
    </BasicModal>
  );
};

export default PasswordModal;
